import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import { TransactionType } from "../../../../models/TransactionType";
import toastOptions from "../../../../utils/toastOptions";
import { getValidator } from "../../../../utils/validatorsYup";
import { dateMask, handleFocus } from "../../../../utils/masks";
import CurrencyInput from "../../../../components/CurrencyInput";
import CongregationSelect from "../../../../components/congregation-select";
import AutoComplete from "../../../../components/AutoComplete";
import moment from "../../../../utils/momentConfig";

import "react-toastify/dist/ReactToastify.css";
import "../../../shared/card.css";
import { EXPENSE_CATEGORIES, FinancialCategory, INCOME_CATEGORIES } from "../../../../models/enums/FinancialCategory";
import { ICreateTransactionDTO } from "../../../../models/TransactionDTO";
import { InstallmentFrequency } from "../../../../models/enums/InstallmentFrequency";
import IAutoCompleteObject from "../../../../models/IAutoCompleteObject";

export default function TransactionAddEdit() {
  const { id } = useParams<"id">();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<ICreateTransactionDTO>({} as ICreateTransactionDTO);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInstallment, setIsInstallment] = useState<boolean>(false);
  const [isReceived, setIsReceived] = useState<boolean>(true);
  const [isAnonymousMember, setIsAnonymousMember] = useState<boolean>(false);
  const [memberSuggestions] = useState<IAutoCompleteObject[]>([]);
  const [memberAutoCompleteValue, setMemberAutoCompleteValue] = useState<string>("");
  const initialType = searchParams.get("type") as TransactionType | null;
  const hasInitialType = !!initialType && Object.values(TransactionType).includes(initialType);
  const memberIncomeRequiredCategories = [
    FinancialCategory.TITHE,
    FinancialCategory.VOW,
    FinancialCategory.OFERRING
  ];

  const schema = yup.object().shape({
    description: yup
      .string()
      .when("category", {
        is: FinancialCategory.OTHER,
        then: yup
          .string()
          .required("Descrição obrigatória")
          .max(200, (val) => `Máximo ${val.max} caracteres`),
        otherwise: yup
          .string()
          .nullable()
          .notRequired()
          .max(200, (val) => `Máximo ${val.max} caracteres`)
      })
      .nullable(),
    anonymousName: yup
      .string()
      .nullable()
      .notRequired()
      .max(200, (val) => `Máximo ${val.max} caracteres`),
    amount: getValidator({ isNumeric: true, numberGreaterThanZero: true }).required("Valor obrigatório"),
    dueDate: getValidator({ isDate: true }).nullable().notRequired(),
    paidAt: getValidator({ isDate: true }).nullable().notRequired(),
    type: yup
      .mixed<TransactionType>()
      .oneOf(Object.values(TransactionType), "Tipo obrigatório")
      .required("Tipo obrigatório"),
    category: yup
      .mixed<FinancialCategory>()
      .oneOf(Object.values(FinancialCategory), "Categoria obrigatória")
      .required("Categoria obrigatória"),
    idCongregation: getValidator({ isNumeric: true }).nullable().notRequired(),
    idMember: getValidator({ isNumeric: true }).when(["category", "type", "isAnonymousMember"], {
      is: (category?: FinancialCategory, type?: TransactionType, anonymousMember?: boolean) =>
        !!category &&
        type === TransactionType.INCOME &&
        memberIncomeRequiredCategories.includes(category) &&
        !anonymousMember,
      then: getValidator({ isNumeric: true }).required("Membro obrigatório"),
      otherwise: getValidator({ isNumeric: true }).nullable().notRequired()
    }),
    isAnonymousMember: yup.boolean().notRequired(),
    isRecurring: yup.boolean().notRequired(),
    isInstallment: yup.boolean().notRequired(),
    noOfTotalInstallments: getValidator({ isNumeric: true }).when("isInstallment", {
      is: true,
      then: getValidator({ isNumeric: true }).required("Nº parcelas obrigatório"),
      otherwise: getValidator({ isNumeric: true }).nullable().notRequired()
    }),
    installmentFrequency: yup.mixed<InstallmentFrequency>().when("isInstallment", {
      is: true,
      then: yup
        .mixed<InstallmentFrequency>()
        .oneOf(Object.values(InstallmentFrequency), "Recorrência obrigatória")
        .required("Recorrência obrigatória"),
      otherwise: yup
        .mixed<InstallmentFrequency>()
        .oneOf(Object.values(InstallmentFrequency))
        .nullable()
        .notRequired()
    }),
    referenceNumber: yup.string().nullable().notRequired(),
    attachmentUrl: yup.string().url("URL inválida").nullable().notRequired()
  });

  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    trigger,
    reset
  } = useForm({ resolver: yupResolver(schema) });

  const toDateInput = (value?: Date | string) => value ? moment(value).format("DD/MM/YYYY") : "";

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        if (!id) {
          const today = moment().toDate();
          const defaultType = hasInitialType && initialType ? initialType : undefined;
          setMemberAutoCompleteValue("");
          setIsReceived(true);
          setIsAnonymousMember(false);

          setTransaction((prev) => ({
            ...prev,
            isRecurring: false,
            dueDate: today,
            paidAt: today,
            ...(defaultType ? { type: defaultType } : {})
          }));
          setIsInstallment(false);          
        }

        if (id) {
          const { data: transactionData } = await api.get(`/transactions/${id}`);
          const item = transactionData?.item ?? transactionData;
          const hasInstallmentData = !!(
            item?.installmentNumber ||
            item?.noOfTotalInstallments ||
            item?.numOfInstallments ||
            item?.installmentTotal ||
            item?.installmentFrequency
          );

          const nextTransaction = {
            ...item,
            isRecurring: item?.isRecurring,
            dueDate: item?.dueDate,
            paidAt: item?.paidAt,
            installmentNumber: item?.installmentNumber,
            noOfTotalInstallments: item?.noOfTotalInstallments ?? item?.numOfInstallments ?? item?.installmentTotal,
            installmentFrequency: item?.installmentFrequency
          } as ICreateTransactionDTO;

          const memberIdFromTransaction = item?.member?.id;

          const memberLabelFromTransaction = item?.member?.name ?? "";
          const shouldMarkAsAnonymousMember =
            item?.type === TransactionType.INCOME &&
            memberIncomeRequiredCategories.includes(item?.category as FinancialCategory) &&
            !memberIdFromTransaction;
          
          if (memberLabelFromTransaction) {
            setMemberAutoCompleteValue(memberLabelFromTransaction);
          } else if (memberIdFromTransaction) {
            try {
              const { data: memberData } = await api.get(`members/${memberIdFromTransaction}`);
              const member = memberData?.item ?? memberData;
              setMemberAutoCompleteValue(member?.name ?? "");
            } catch {
              setMemberAutoCompleteValue("");
            }
          } else {
            setMemberAutoCompleteValue("");
          }

          setTransaction(nextTransaction);
          setIsInstallment(hasInstallmentData);
          setIsReceived(!!nextTransaction.paidAt);
          setIsAnonymousMember(shouldMarkAsAnonymousMember);
          reset({
            isRecurring: nextTransaction.isRecurring,
            isInstallment: hasInstallmentData,
            type: nextTransaction.type,
            category: nextTransaction.category,
            anonymousName: item?.anonymousName ?? "",
            idCongregation: nextTransaction.idCongregation,
            description: nextTransaction.description,
            amount: nextTransaction.amount,
            dueDate: toDateInput(nextTransaction.dueDate),
            paidAt: toDateInput(nextTransaction.paidAt),
            noOfTotalInstallments: nextTransaction.noOfTotalInstallments,
            installmentFrequency: nextTransaction.installmentFrequency,
            referenceNumber: item?.referenceNumber ?? "",
            idMember: memberIdFromTransaction,
            isAnonymousMember: shouldMarkAsAnonymousMember
          });

          setValue("idMember", memberIdFromTransaction);
        }
      } catch (e: any) {
        e.messages?.forEach((m: string) => {
          toast.error(m, toastOptions);
        });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [hasInitialType, id, initialType, reset]);

  const createTitle = useMemo(() => {
    if (transaction.type === TransactionType.INCOME) {
      return "Nova Receita";
    }

    if (transaction.type === TransactionType.EXPENSE) {
      return "Nova Despesa";
    }

    return "Nova Transação";
  }, [transaction.type]);

  const saveButtonClass = useMemo(() => {
    if (transaction.type === TransactionType.INCOME) {
      return "uk-button-success";
    }

    if (transaction.type === TransactionType.EXPENSE) {
      return "uk-button-danger";
    }

    return "uk-button-primary";
  }, [transaction.type]);

  const typeLabelClass = useMemo(() => {
    if (transaction.type === TransactionType.INCOME) {
      return "uk-label-success";
    }

    if (transaction.type === TransactionType.EXPENSE) {
      return "uk-label-danger";
    }

    return "uk-label";
  }, [transaction.type]);

  const isIncomeType = transaction.type === TransactionType.INCOME;
  const isMemberRequiredForCategory =
    isIncomeType &&
    memberIncomeRequiredCategories.includes(transaction.category as FinancialCategory);

  const availableCategories = useMemo(() => {
    if (transaction.type === TransactionType.INCOME) {
      return INCOME_CATEGORIES;
    }

    if (transaction.type === TransactionType.EXPENSE) {
      return EXPENSE_CATEGORIES;
    }

    return Object.values(FinancialCategory);
  }, [transaction.type]);

  useEffect(() => {
    if (!transaction.category) {
      return;
    }

    if (!availableCategories.includes(transaction.category)) {
      const fallbackCategory = availableCategories[0];
      if (fallbackCategory) {
        setTransaction((prev) => ({ ...prev, category: fallbackCategory }));
        setValue("category", fallbackCategory);
      }
    }
  }, [availableCategories, setValue, transaction.category]);

  useEffect(() => {
    const dueDateValue = transaction.dueDate ? moment(transaction.dueDate).format("DD/MM/YYYY") : "";

    if (isReceived) {
      setValue("paidAt", dueDateValue);
      return;
    }

    setValue("paidAt", "");
  }, [isReceived, setValue, transaction.dueDate]);

  useEffect(() => {
    if (!transaction.isRecurring) {
      return;
    }

    if (isInstallment) {
      setIsInstallment(false);
    }

    setValue("isInstallment", false);
    setValue("noOfTotalInstallments", undefined);
    setValue("installmentFrequency", undefined);

    setTransaction((prev) => ({
      ...prev,
      noOfTotalInstallments: undefined,
      installmentFrequency: undefined
    }));
  }, [isInstallment, setValue, transaction.isRecurring]);

  useEffect(() => {
    if (isMemberRequiredForCategory) {
      return;
    }

    if (isAnonymousMember) {
      setIsAnonymousMember(false);
      setValue("isAnonymousMember", false);
      setValue("anonymousName", "");
    }
  }, [isAnonymousMember, isMemberRequiredForCategory, setValue]);

  const onSubmit = async (data: ICreateTransactionDTO) => {
    try {
      setIsSaving(true);
      const paidAtValue = isReceived
        ? data.paidAt ?? (transaction.dueDate ? moment(transaction.dueDate).format("DD/MM/YYYY") : undefined)
        : null;

      const payload: any = {
        ...data,
        isAnonymous: isAnonymousMember,
        paidAt: paidAtValue
      };

      delete payload.isAnonymousMember;

      if (!isAnonymousMember) {
        delete payload.anonymousName;
      }

      if (id) {
        await api.put(`/transactions/${id}`, payload);
        toast.success("Transação atualizada com sucesso!", toastOptions);
      } else {
        await api.post("/transactions", payload);
        toast.success("Transação cadastrada com sucesso!", toastOptions);
        reset();
        setTransaction({} as ICreateTransactionDTO);
      }

      navigate("/financeiro/transacoes");
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div id="page-financial-transaction" className="uk-section uk-section-muted uk-padding-small">
      <div className="uk-container">
        <div className="uk-grid-small uk-margin-bottom" uk-grid="true">
          <div className="uk-width-expand">
            <h3 className="uk-card-title uk-margin-remove-bottom">{id ? "Editar Transação" : createTitle}</h3>
            {transaction.type && <span className={`uk-label ${typeLabelClass} uk-text-bold uk-text-small`}>{transaction.type}</span>}
            <p className="uk-text-meta uk-margin-remove">
              {!transaction.type && "Registre uma movimentação financeira."}
            </p>
          </div>
          <div className="uk-width-auto">
            <button
              type="button"
              className="uk-button uk-button-default uk-button-small"
              onClick={() => navigate(-1)}
            >
              Voltar
            </button>
          </div>
        </div>

        <div className="uk-grid-small" uk-grid="true">
          <div className="uk-width-1@m">
            <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-position-relative">
              {loading && (
                <div
                  className="uk-position-cover uk-overlay-primary uk-flex uk-flex-center uk-flex-middle"
                  style={{ zIndex: 20 }}
                >
                  <div className="uk-spinner" style={{ width: 48, height: 48 }} />
                </div>
              )}
              <div className="uk-card-header">
                <h4 className="uk-card-title uk-margin-remove-bottom">Dados da Transação</h4>
              </div>
              <div className="uk-card-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    return handleSubmit<ICreateTransactionDTO>(onSubmit)(e);
                  }}
                >
                  <div className="uk-grid-small" uk-grid="true">
                    <div className="uk-width-1-6@m">
                      <label className="uk-form-label" htmlFor="dueDate">Data</label>
                      <input
                        className="uk-input uk-form-small"
                        type="text"
                        id="dueDate"
                        name="dueDate"
                        ref={register}
                        placeholder="99/99/9999"
                        defaultValue={transaction.dueDate ? moment(transaction.dueDate).format("DD/MM/YYYY") : ""}
                        autoComplete="off"
                        onFocus={handleFocus}
                        onChange={(e) => {
                          e.target.value = dateMask(e.target.value);
                        }}
                      />
                      <small className="uk-text-danger">{errors.dueDate?.message}</small>
                    </div>

                    {!hasInitialType && !id && (
                      <div className="uk-width-1-6@m">
                        <label className="uk-form-label" htmlFor="type">Tipo</label>
                        <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                          <select
                            className="uk-form-small"
                            id="type"
                            name="type"
                            ref={register}
                            value={transaction.type ?? ""}
                            disabled={!!id}
                            onChange={(e) => {
                              const value = e.target.value as TransactionType;

                              setTransaction((prev) => ({
                                ...prev,
                                type: value || undefined,
                                dueDate: value === TransactionType.INCOME ? undefined : prev.dueDate
                              }));
                              setValue("type", value);
                              if (value === TransactionType.INCOME) {
                                setValue("dueDate", "");
                              }
                            }}
                          >
                            <option value="">Selecione...</option>
                            {Object.values(TransactionType).map((value) => (
                              <option key={value} value={value}>{value}</option>
                            ))}
                          </select>
                          <button
                            className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                            style={{ textTransform: "none" }}
                            type="button"
                            disabled={!!id}
                            tabIndex={-1}
                          >
                            <span>{(transaction?.type as TransactionType) || "Selecione..."}</span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                        <small className="uk-text-danger">{errors.type?.message}</small>
                      </div>
                    )}

                    {(hasInitialType || !!id) && (
                      <input
                        type="hidden"
                        name="type"
                        ref={register}
                        defaultValue={transaction.type ?? initialType ?? ""}
                      />
                    )}

                    <div className="uk-width-1-3@m">
                      <label className="uk-form-label" htmlFor="category">Categoria</label>
                      <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                        <select
                          className="uk-form-small"
                          id="category"
                          name="category"
                          ref={register}
                          value={transaction.category ?? ""}
                          onChange={(e) => {
                            const value = e.target.value as FinancialCategory;
                            setTransaction((prev) => ({ ...prev, category: value || undefined }));
                            setValue("category", value);
                          }}
                        >
                          <option value="">Selecione...</option>
                          {availableCategories.map((value) => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </select>
                        <button
                          className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                          style={{ textTransform: "none" }}
                          type="button"
                          tabIndex={-1}
                        >
                          <span>{(transaction?.category as FinancialCategory) || "Selecione..."}</span>
                          <span uk-icon="icon: chevron-down"></span>
                        </button>
                      </div>
                      <small className="uk-text-danger">{errors.category?.message}</small>
                    </div>

                    <div className="uk-width-1-3@m">
                      <CongregationSelect
                        id="idCongregation"
                        name="idCongregationSelect"
                        label="Congregação"
                        emptyOptionLabel="Selecione..."
                        selectedId={transaction.idCongregation}
                        wrapperClassName="uk-width-1-1"
                        selectWrapperClassName="uk-width-1"
                        afterSelect={(idCongregation) => {
                          const nextCongregationId = idCongregation > 0 ? idCongregation : undefined;
                          setTransaction((prev) => ({ ...prev, idCongregation: nextCongregationId }));
                          setValue("idCongregation", nextCongregationId);
                        }}
                      />
                      <input
                        type="hidden"
                        name="idCongregation"
                        ref={register}
                        defaultValue={transaction.idCongregation ?? ""}
                      />
                      <small className="uk-text-danger">{errors.idCongregation?.message}</small>
                    </div>

                    <div className="uk-width-1@m uk-flex uk-flex-middle">
                      <label className="uk-form-label uk-margin-small-right" htmlFor="isReceived">
                        {isIncomeType ? "Recebido?" : "Pago?"}
                      </label>
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        id="isReceived"
                        checked={isReceived}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsReceived(checked);

                          if (checked) {
                            const dueDateValue = transaction.dueDate ? moment(transaction.dueDate).format("DD/MM/YYYY") : "";
                            setValue("paidAt", dueDateValue);
                            return;
                          }

                          setValue("paidAt", "");
                        }}
                      />
                      <input
                        type="hidden"
                        id="paidAt"
                        name="paidAt"
                        ref={register}
                        defaultValue={toDateInput(transaction.paidAt)}
                      />
                    </div>

                    {isMemberRequiredForCategory && (
                      <div className="uk-width-1@m">
                        <div className="uk-flex uk-flex-middle uk-margin-small-bottom">
                          <label className="uk-form-label uk-margin-small-right" htmlFor="isAnonymousMember">
                            Anônimo / Não cadastrado?
                          </label>
                          <input
                            className="uk-checkbox"
                            type="checkbox"
                            id="isAnonymousMember"
                            name="isAnonymousMember"
                            ref={register}
                            checked={isAnonymousMember}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setIsAnonymousMember(checked);
                              setValue("isAnonymousMember", checked);

                              if (checked) {
                                setMemberAutoCompleteValue("");
                                setTransaction((prev) => ({ ...prev, idMember: undefined }));
                                setValue("idMember", undefined);
                                return;
                              }

                              setValue("anonymousName", "");
                              setTransaction((prev) => ({ ...prev, anonymousName: undefined }));
                            }}
                          />
                        </div>

                        {isAnonymousMember && (
                          <div className="uk-margin-small-bottom">
                            <label className="uk-form-label" htmlFor="anonymousMember">Nome (opcional)</label>
                            <input
                              className="uk-input uk-form-small uk-text-uppercase"
                              type="text"
                              id="anonymousMember"
                              name="anonymousName"
                              ref={register}
                              defaultValue={transaction.anonymousName ?? ""}
                              autoComplete="off"
                              style={{ textTransform: "uppercase" }}
                            />
                            <small className="uk-text-danger">{errors.anonymousName?.message}</small>
                          </div>
                        )}

                        {!isAnonymousMember && (<>
                          <label className="uk-form-label" htmlFor="idMember">Membro</label>
                          <AutoComplete
                            urlFetch="members/name/"
                            suggestions={memberSuggestions}
                            defaultValue={memberAutoCompleteValue}
                            onAfterChange={(selected?: IAutoCompleteObject) => {
                              const nextMemberId = selected?.id;
                              setMemberAutoCompleteValue(selected?.label ?? "");
                              setTransaction((prev) => ({ ...prev, idMember: nextMemberId }));
                              setValue("idMember", nextMemberId);
                            }}
                          />
                        </>)}
                        <input
                          type="hidden"
                          id="idMember"
                          name="idMember"
                          ref={register}
                          defaultValue={transaction.idMember ?? ""}
                        />
                        <small className="uk-text-danger">{errors.idMember?.message}</small>
                      </div>
                    )}

                    <div className="uk-width-3-4@m">
                      <label className="uk-form-label" htmlFor="description">Descrição</label>
                      <input
                        className="uk-input uk-form-small uk-text-uppercase"
                        type="text"
                        id="description"
                        name="description"
                        ref={register}
                        defaultValue={transaction.description ?? ""}
                        autoComplete="off"
                        style={{ textTransform: "uppercase" }}
                      />
                      <small className="uk-text-danger">{errors.description?.message}</small>
                    </div>

                    <div className="uk-width-1-4@m">
                      <CurrencyInput
                        name="amount"
                        label="Valor"
                        placeholder="Valor"
                        defaultValue={transaction?.amount}
                        register={register}
                        setValue={setValue}
                        trigger={trigger}
                        onChange={(value) => setTransaction({...transaction, amount: value})}
                        error={errors.amount?.message}
                      />
                    </div>

                    <div className="uk-width-1@m uk-flex uk-flex-middle">
                      <label className="uk-form-label uk-margin-small-right" htmlFor="isRecurring">Recorrente?</label>
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        id="isRecurring"
                        name="isRecurring"
                        ref={register}
                        checked={!!transaction.isRecurring}
                        disabled={!!isInstallment}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setTransaction((prev) => ({ ...prev, isRecurring: checked }));
                          setValue("isRecurring", checked);
                        }}
                      />
                    </div>

                    <div className="uk-width-1@m uk-flex uk-flex-middle">
                      <label className="uk-form-label uk-margin-small-right" htmlFor="isInstallment">Parcelado?</label>
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        id="isInstallment"
                        name="isInstallment"
                        ref={register}
                        checked={isInstallment}
                        disabled={!!transaction.isRecurring}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsInstallment(checked);
                          setValue("isInstallment", checked);

                          if (!checked) {
                            setValue("noOfTotalInstallments", undefined);
                            setValue("installmentFrequency", undefined);
                            setTransaction((prev) => ({
                              ...prev,
                              noOfTotalInstallments: undefined,
                              installmentFrequency: undefined
                            }));
                          }
                        }}
                      />
                    </div>

                    {isInstallment && (<>
                      <div className="uk-width-1-6@m">
                        <label className="uk-form-label" htmlFor="noOfTotalInstallments">Nº parcelas</label>
                        <input
                          className="uk-input uk-form-small"
                          type="number"
                          id="noOfTotalInstallments"
                          name="noOfTotalInstallments"
                          ref={register}
                          defaultValue={(transaction as any).noOfTotalInstallments ?? (transaction as any).numOfInstallments ?? (transaction as any).installmentTotal ?? ""}
                          min={1}
                        />
                        <small className="uk-text-danger">{errors.noOfTotalInstallments?.message}</small>
                      </div>

                      <div className="uk-search uk-search-default uk-width-1-4@m">
                        <label className="uk-form-label uk-margin-small-right" htmlFor="installmentFrequency">Recorrência</label>
                        <div className="uk-form-controls uk-width-expand">
                          <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                            <select
                              className="uk-select uk-form-small"
                              id="installmentFrequency"
                              name="installmentFrequency"
                              ref={register}
                              value={transaction.installmentFrequency ?? ""}
                              onChange={(e) => {
                                const value = e.target.value as InstallmentFrequency;
                                setTransaction((prev) => ({ ...prev, installmentFrequency: value }));
                                setValue("installmentFrequency", value);
                              }}
                            >
                              <option value="">Selecione...</option>
                              {Object.values(InstallmentFrequency).map((value) => (
                                <option key={value} value={value}>{value}</option>
                              ))}
                            </select>
                            <button
                              className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                              style={{ textTransform: "none" }}
                              type="button"
                              tabIndex={-1}
                            >
                              <span>
                                {(transaction.installmentFrequency as InstallmentFrequency) || "Selecione..."}
                              </span>
                              <span uk-icon="icon: chevron-down"></span>
                            </button>
                          </div>
                        </div>
                        <small className="uk-text-danger">{errors.installmentFrequency?.message}</small>
                      </div>
                    </>)}

                    <div className="uk-width-1-3@m">
                      <label className="uk-form-label" htmlFor="referenceNumber">Número de Referência</label>
                      <input
                        className="uk-input uk-form-small"
                        type="text"
                        id="referenceNumber"
                        name="referenceNumber"
                        ref={register}
                        placeholder="Número de Referência"
                        defaultValue={transaction.referenceNumber ?? ""}
                        autoComplete="off"
                      />
                      <small className="uk-text-danger">{errors.referenceNumber?.message}</small>
                    </div>
                  </div>

                  <div className="uk-margin-top uk-flex uk-flex-right">
                    <button
                      type="submit"
                      className={`uk-button uk-button-small ${saveButtonClass}`}
                      disabled={formState.isSubmitting || isSaving}
                    >
                      {(formState.isSubmitting || isSaving) && <span uk-spinner="true; ratio:0.5"></span>} 
                      Salvar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
